import sort from 'deep-sort-object';

import ManagerRepository from 'coreClasses/ManagerRepository';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {DEFAULT_SEARCH_PARAMS, FIRST_PAGE, NEXT_DIRECTION, REFINEMENT_DISALLOW_LIST} from 'brandConstants';
import {getConfig, isServer, toBase64} from 'coreUtils/utils';

import {
	ProductSearchHit,
	ProductSearchParams,
	ProductSearchResultState as ProductSearchResultType,
} from 'coreStore/data/productSearchResult/interface';
import {
	flushProductSearchResult,
	receiveProductSearchResult,
	selectProductSearchResultByKey,
	setUserSelectedRefinements,
	setUserSelectedSorting,
} from 'coreStore/data/productSearchResult';
import {storeGridSize} from 'coreStore/data/productSearchResult';

import PlpUrlModifier from 'coreUtils/plp/url';
import {decodeUriComponentWithTryCatch} from 'coreUtils/url';

import {
	ESearchProvider,
	ESearchType,
	IContentCardConfig,
	IContentCards,
	ICustomElementIncludes,
	IPopularSearchesParams,
	ISearchOptions,
	ISearchParams,
	ISearchRefinements,
	ISearchSuggestionsParams,
} from '../ISearch';
import SearchModel from '../SearchModel';
import RefinementValueModel from '../RefinementValueModel';
import RefinementModel from '../RefinementModel';
import GridModel from '../GridModel';
import {receiveProducts, selectProduct} from 'coreStore/data/products';

/**
 * Represents AbstractLogProvider
 */
abstract class AbstractSearchProvider {
	protected userSelectedRefinements: ISearchRefinements = {};
	protected userSelectedRefinementsToBeTranslated: Map<string, string>;
	protected changedLocale: boolean;
	protected isSearchRedirectInAnotherLang: boolean;
	protected userSelectedSorting: string;
	protected userSelectedPage: number;
	//TODO: move pageNumber to searchModel
	protected pageNumber: number;
	protected searchQuery: string;
	protected limit: number;
	protected key: string;
	protected pageUrl: string;
	protected contentCards: IContentCards;
	protected customElementIncludes = new Map() as ICustomElementIncludes;
	protected appendWildcardQuery: boolean = false;
	protected searchType: ESearchType;
	protected grid: GridModel;
	protected managerRepository: ManagerRepository;
	public cacheKey: string;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {String} key - key
	 * @param {string} cacheKey - search instance cache key ( used in redux and class cache )
	 * @param {ESearchType} searchType - search type
	 */
	constructor(managerRepository: ManagerRepository, key: string, cacheKey: string, searchType: ESearchType) {
		this.managerRepository = managerRepository;
		this.key = key;
		this.cacheKey = cacheKey;
		this.limit = DEFAULT_SEARCH_PARAMS.limit;
		this.userSelectedRefinementsToBeTranslated = new Map();
		this.changedLocale = false;
		this.isSearchRedirectInAnotherLang = false;
		this.grid = new GridModel(this);
		this.searchType = searchType;
	}

	protected abstract fetchSearch(query: ProductSearchParams): Promise<ProductSearchResultType>;

	/**
	 * Returns the SearchModel based on the given parameters
	 * If the SearchModel is not in store, it is fetched, stored and then returned
	 *
	 * @param {ISearchParams} params Search Params
	 * @param {ISearchOptions} options Search Options
	 * @returns {Promise<SearchModel>}
	 */
	public abstract search(params?: ISearchParams, options?: ISearchOptions): Promise<SearchModel>;

	/**
	 * Returns the suggestions based on the given parameters
	 *
	 * @param {ISearchSuggestionsParams} params Search Suggestions Params
	 * @returns {Promise<string[]>}
	 */
	public abstract suggestions(params?: ISearchSuggestionsParams): Promise<string[]>;

	/**
	 * Returns the list of popular search
	 *
	 * @returns {Promise<string[]>}
	 */
	public abstract popularSearches(params?: IPopularSearchesParams): Promise<string[]>;

	/**
	 * Translate refinements when change language
	 *
	 * @returns {Promise<void>}
	 */
	public abstract translateRefinements(): Promise<void>;

	/**
	 * Translates Refinements
	 * @returns {Promise<void>}
	 */
	public abstract translateSelectedRefinements(): Promise<void>;

	/**
	 * Stores the given search result.
	 * @param {ProductSearchResultType} productSearchResult The response to store
	 * @param {string} queryKey The queryKey to be used in the store. This is needed to take a fresh value if the refinements or the searchQuery changes
	 * @protected
	 * @returns void
	 */
	protected storeSearch(productSearchResult: ProductSearchResultType, queryKey: string) {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveProductSearchResult({
			productSearchResult,
			userSelectedRefinements: this.userSelectedRefinements,
			key: this.cacheKey,
			queryKey,
			gridSize: this.grid.getGridSize(),
			limit: this.getLimit(),
		}));

		if (this.managerRepository.getConfigurationsMgr().get('sitePreferences.enableEnhancedSearchProductResult')) {
			const products = {};
			const store = this.managerRepository.getStoreMgr().getStore();

			productSearchResult.hits?.forEach((hit: ProductSearchHit) => {
				const existingProduct = selectProduct(store.getState(), hit.productId);

				if (existingProduct?.ready && !existingProduct?.type?.placeholder) {
					return;
				}

				const type: {variationGroup?: boolean; set?: boolean} = {};

				if (hit.hitType === 'variation_group') {
					type.variationGroup = true;
				}

				if (hit.hitType === 'set' || hit.productType?.set) {
					type.set = true;
				}

				const productData: Record<string, unknown> = {
					...hit,
					variationGroups: hit.c_variationGroups,
					brand: hit.c_brand,
					inventories: [hit.c_inventory],
					inventory: hit.c_inventory,
					master: hit.c_master,
					variationValues: {
						...hit?.variants?.find(variant =>
							variant.productId === hit.representedProduct.id
						)?.variationValues
					},
					stepQuantity: 1,
					minOrderQuantity: 1,
					unit: '1',
					unitQuantity: 0,
					name: hit.productName,
					slugUrl: hit.c_url,
					primaryCategoryId: hit.c_primaryCategory,
					type,
					...hit.representedProduct,
					id: hit.productId,
					ready: true,
					isFetchedOnSSR: isServer
				};

				if (type.set && hit.representedProducts?.length) {
					const fullProduct = this.managerRepository.getProductsMgr().getProduct(hit.productId);
					productData.setProducts = (fullProduct as any)?.setProducts;
				}

				products[hit.productId] = productData;
			});

			this.managerRepository.getStoreMgr().getStore().dispatch(receiveProducts(products));
		}
	}

	/**
	 * Generates a base64 key based on the given query
	 * Note: Language and country are needed because on language/country change we want the stored search to be totally wiped.
	 * @param {ProductSearchParams} query
	 * @returns string
	 */
	getKeyByQuery(query: ProductSearchParams): string {
		if (query.refine) {
			//TODO: sort in the model to achieve best caching
			query.refine.sort();
		}

		return toBase64(JSON.stringify(sort(Object.assign({}, query, {limit: null, offset: null}))));
	};

	/**
	 * Clears the storage
	 * @returns void
	 */
	public clearStoredSearch() {
		this.managerRepository.getStoreMgr().getStore().dispatch(flushProductSearchResult(this.cacheKey));
	}

	/**
	 * Set search query
	 * @param {Object} query
	 * @returns {void}
	 */
	public setSearchQuery(query): void {
		this.searchQuery = query;
	}

	/**
	 * Returns current search query
	 * @returns {string}
	 */
	public getSearchQuery(): string {
		return this.searchQuery;
	}

	/**
	 * Returns the current instance key
	 */
	public getKey() {
		return this.key;
	}

	/**
	 * Returns the grid model
	 */
	public getGridModel() {
		return this.grid;
	}

	/**
	 * Set page url
	 * @param {string} pageUrl
	 */
	public setPageUrl(pageUrl: string) {
		this.pageUrl = pageUrl;
	}

	/**
	 * Get page url
	 * @returns {string}
	 */
	public getPageUrl(): string {
		return this.pageUrl;
	}

	/**
	 * Set search limit
	 * @param {number} limit
	 * @returns {void}
	*/
	public setLimit(limit: number): void {
		this.limit = limit;
	}

	/**
	 * Returns search limit
	 * @returns number
	 */
	public getLimit(): number {
		return this.limit;
	}

	/**
	 * Returns a stored search raw result
	 * @protected
	 * @returns ProductSearchResultType
	 */
	protected getStoredSearch(): ProductSearchResultType {
		const store = this.managerRepository.getStoreMgr().getStore();

		return selectProductSearchResultByKey(store.getState(), this.cacheKey);
	}

	/**
	 * Returns a stored SearchModel.
	 * @returns SearchModel
	 */
	public getStoredSearchModel(): SearchModel {
		const storedSearch = this.getStoredSearch();

		return storedSearch ? new SearchModel(this, this.managerRepository, storedSearch, this.getLimit()) : null;
	}

	/**
	 * Set page number
	 * @param {number} pageNumber
	 * @returns {void}
	 */
	public setPageNumber(pageNumber: number): void {
		if (typeof window !== 'undefined') {
			if (this.pageNumber !== pageNumber) {
				this.pageNumber = pageNumber;

				const searchModel: SearchModel = this.getStoredSearchModel();
				PlpUrlModifier.updateUrl(
					this.pageUrl,
					pageNumber,
					searchModel?.getSelectedSortingOption() || '',
					searchModel?.getSelectedRefinements() || [],
				);
			}
		}
	}

	/**
	 * Get page number
	 * @returns {number | undefined} The page number or undefined if running on server
	 */
	public getPageNumber(): number | undefined {
		if (typeof window !== 'undefined') {
			return this.pageNumber;
		}

		return undefined;
	}

	/**
	 * Returns the contentCards json
	 * @returns IContentCards
	 */
	public getContentCards(): IContentCards {
		return this.contentCards;
	}

	/**
	 * Sets the contentCards json
	 * @param contentCards {object[]} - Contentful objects
	 * @param config {IContentCardConfig[]} - Configurations
	 * @returns void
	 */
	public setContentCards(
		contentCards: object[],
		config: IContentCardConfig[]
	): void {
		this.contentCards = {};

		contentCards?.forEach((c, k) => {
			const cardConfig = config[k];

			if (cardConfig) {
				this.contentCards[k] = {
					contentfulData: c,
					size: cardConfig.size,
					height: cardConfig.height,
					width: cardConfig.width,
					position: cardConfig.position
				};
			}
		});
	}

	/**
	 * Convert refinement string attributes to array
	 * @param {object} refinements
	 * @returns
	 */
	public parseRefinements = (refinements: object): ISearchRefinements => {
		const newRefinements: ISearchRefinements = {};

		Object.keys(refinements || {}).forEach((key) => {
			const refinementsKey: string = refinements[key];

			newRefinements[key] = (typeof refinementsKey === 'string') ? refinementsKey.split('|') : refinementsKey;
			newRefinements[key] = newRefinements[key].map(el => decodeUriComponentWithTryCatch(el.replace(/\+/g, ' ')));
		});

		return newRefinements;
	};

	/**
	 * Set user selected refinements
	 * @param {Object} userSelectedRefinements
	 * @returns {void}
	 */
	public setUserSelectedRefinements(userSelectedRefinements: object): void {
		this.userSelectedRefinements = this.parseRefinements(userSelectedRefinements);
		this.managerRepository.getStoreMgr().getStore().dispatch(setUserSelectedRefinements({
			key: this.cacheKey,
			userSelectedRefinements: this.userSelectedRefinements
		}));
	}

	/**
	 * @returns {ISearchRefinements}
	 */
	public getUserSelectedRefinements(): ISearchRefinements {
		return this.userSelectedRefinements || {};
	}

	/**
	 * Remove all selected refinements
	 * @returns {void}
	 */
	public clearRefinements(): void {
		this.resetToFirstPage();
		this.setUserSelectedPage(FIRST_PAGE);

		const newRefinements = Object.assign({}, this.userSelectedRefinements);

		Object.keys(newRefinements)?.forEach((key) =>
			REFINEMENT_DISALLOW_LIST.includes(key) || delete newRefinements[key]);

		this.setUserSelectedRefinements(newRefinements);
		this.setUserSelectedSorting('');
	}

	/**
	 * Add new refinements
	 * to the ones already selected
	 * @param {RefinementValueModel} refinementValue
	 * @returns {void}
	 */
	public addRefinement(refinementValue: RefinementValueModel): void {
		const newRefinements = Object.assign({}, this.userSelectedRefinements);
		const refinementId = refinementValue.getAttributeId();

		if (refinementValue.getValue()) {
			if (refinementId in newRefinements) {
				newRefinements[refinementId].push(refinementValue.getValue());
			} else {
				newRefinements[refinementId] = [refinementValue.getValue()];
			}

			this.setUserSelectedRefinements(newRefinements);
		}
	}

	/**
	 * Remove a refinement
	 * from the ones already selected
	 * @param {RefinementValueModel} refinementValue - the refinement value to remove
	 * @returns {void}
	 */
	public removeRefinement(refinementValue: RefinementValueModel): void {
		const newRefinements = Object.assign({}, this.userSelectedRefinements);

		const refinementId = refinementValue.getAttributeId();

		if (refinementId in newRefinements) {
			newRefinements[refinementId] =
				newRefinements[refinementId].filter((v) => v !== refinementValue.getValue());

			this.setUserSelectedRefinements(newRefinements);
		}
	}

	/**
	 * Remove a specific refinement
	 * @param {RefinementModel} refinementModel
	 * @param {boolean} excludeApplied
	 */
	public clearRefinement(refinementModel: RefinementModel, excludeApplied: boolean = false) {
		const newRefinements = Object.assign({}, this.userSelectedRefinements);

		const clearRefinement = (rm: RefinementModel) => {
			if (!rm?.getAttributeId()) {
				return;
			}

			if (excludeApplied) {
				const selectedRef = this.getStoredSearchModel()?.
					getSelectedRefinementById(rm.getAttributeId())?.split('|');

				if (selectedRef) {
					newRefinements[rm.getAttributeId()] = newRefinements[rm.getAttributeId()]?.
						filter((refVal) => selectedRef.indexOf(refVal) !== -1);
				} else {
					delete newRefinements[rm.getAttributeId()];
				}
			} else {
				delete newRefinements[rm.getAttributeId()];
			}

			for (const srm of rm.getChildren()){
				clearRefinement(srm);
			}
		};

		clearRefinement(refinementModel);

		this.setUserSelectedRefinements(newRefinements);
	}

	/**
	 * Toggle a single refinement based on its properties
	 * @param refinementValue
	 * @param switchAction
	 * @returns {void}
	 */
	public toggleRefinementValueModel(
		refinementValue: RefinementValueModel,
		switchAction: boolean|null = null
	): void {
		const refinement = refinementValue.getRefinementModel();
		const filterAllIndex = refinement.getFilterAllIndex();
		const parsedSwitchAction = switchAction !== null ? switchAction : !refinementValue.isActive();
		let otherRefinementValues: RefinementValueModel[] = [];

		if (refinementValue.isFilterAll()) {
			otherRefinementValues = refinement.getValues()
				.splice(filterAllIndex, 1);
			otherRefinementValues?.forEach((rv) => rv.isActive() && this.toggleRefinements(rv, false));
		} else if (!refinementValue.isFilterAll() && !refinement.isSingleSelection() && filterAllIndex !== -1) {
			this.toggleRefinements(refinement.getFilterAll(), false);
		} else if (refinement.isSingleSelection()) {
			otherRefinementValues = refinement.getValues()
				.filter((ref) =>
					ref.getValue() !== refinementValue.getValue()
				);

			if (parsedSwitchAction === true) {
				otherRefinementValues?.forEach((rv) => rv.isActive() && this.toggleRefinements(rv, false));
			}
		}

		this.toggleRefinements(refinementValue, parsedSwitchAction);
	}

	/**
	 * Toggle refinement
	 *
	 * @param {RefinementValueModel} refinementValue
	 * @param {boolean|null} switchValue
	 */
	public toggleRefinements( //TODO: Find a different name
		refinementValue: RefinementValueModel,
		switchValue: boolean|null = null
	): void {
		this.resetToFirstPage();
		this.setUserSelectedPage(FIRST_PAGE);

		const toggleValue = switchValue === null ? !refinementValue.isActive() : switchValue;

		if (toggleValue) {
			this.addRefinement(refinementValue);
		} else {
			this.removeRefinement(refinementValue);
		}
	}

	/**
	 * Save Selected refinements presentation id that could be translated on change language
	 * @returns void
	 */
	saveSelectedRefinements() {
		this.userSelectedRefinementsToBeTranslated = new Map();

		this.getStoredSearchModel()?.getSelectedRefinementsValuesModels()
			?.forEach((refinementValueModel) =>
				this.userSelectedRefinementsToBeTranslated
					.set(refinementValueModel.getValue(), refinementValueModel.getPresentationId())
			);
	}

	/**
	 * Returns all translatable refinements models
	 * @param {string} cgid
	 * @param {string[]} selectedRefinementsKey
	 * @returns RefinementModel[]
	 */
	async getRefinementsTranslationsMap(selectedRefinementsKey: string[]) {
		const cgid = this.getStoredSearchModel().getSelectedRefinementById('cgid');
		const searchParams = this.getSearchParamTranslation(cgid, undefined);
		const searchResult = await this.fetchSearch(searchParams);
		const searchModel = new SearchModel(this, this.managerRepository, searchResult, this.getLimit());
		const refinementModelValuesMap = new Map();

		searchModel.getRefinements(false, false)
			.filter((refinementModel) => selectedRefinementsKey.includes(refinementModel.getAttributeId()))
			?.forEach((refinementModel) => {
				const refinementModelValues = refinementModel.getValues();

				if (refinementModelValues?.length) {
					for (const refinementModelValue of refinementModelValues) {
						refinementModelValuesMap.set(refinementModelValue.getPresentationId(),
							refinementModelValue.getValue());
					}
				}
			});

		return refinementModelValuesMap;
	}

	/**
	 * Returns if some filter is applied
	 * @returns {Boolean}
	 */
	public isSomeFilterSelectedByUser(): boolean {
		const userSelectedRefinements = this.getUserSelectedRefinements();

		return Object.keys(userSelectedRefinements)
			.filter((key) => REFINEMENT_DISALLOW_LIST.indexOf(key) === -1
			&& userSelectedRefinements[key]?.length > 0)?.length > 0;
	}

	/**
	 * Set user selected sorting
	 * @param {string} sorting
	 * @returns {void}
	 */
	public setUserSelectedSorting(sorting: string): void {
		this.userSelectedSorting = sorting;
		this.managerRepository.getStoreMgr().getStore().dispatch(setUserSelectedSorting({
			key: this.cacheKey,
			userSelectedSorting: this.userSelectedSorting
		}));
	}

	/**
	 * Toggle sorting
	 * @param {boolean} toggleSorting
	 * @param {string} optionId
	 */
	public toggleSorting(toggleSorting: boolean, optionId: string): void {
		this.resetToFirstPage();
		this.setUserSelectedPage(FIRST_PAGE);
		this.setUserSelectedSorting(toggleSorting ? optionId : '');
	}

	/**
	 * Copies the applied filter and sorting on
	 * the user selected filters and sorting
	 */
	copySelectedOnUserSelected() {
		const storedSearchModel = this.getStoredSearchModel();
		const selectedRefinements = storedSearchModel?.getSelectedRefinements();

		const newUserSelectedRefinements = {};

		Object.keys(selectedRefinements || {})?.forEach((selRefKey) => {
			const selectedRefinement = selectedRefinements[selRefKey]?.split('|');

			newUserSelectedRefinements[selRefKey] = selectedRefinement;
		});

		this.setUserSelectedRefinements(newUserSelectedRefinements);
		this.setUserSelectedSorting(storedSearchModel?.getSelectedSortingOption());
	}

	/**
	 * Load search page
	 * @param {string} direction
	 * @returns number
	 */
	public loadPage(direction: string): number {
		const storedSearchModel = this.getStoredSearchModel();
		const pageToBeLoaded: number =
			direction === NEXT_DIRECTION
				? storedSearchModel?.getNextPageNumberToLoad()
				: storedSearchModel?.getPrevPageNumberToLoad();

		if (pageToBeLoaded && direction === NEXT_DIRECTION) {
			this.setUserSelectedPage(pageToBeLoaded);
			this.setPageNumber(pageToBeLoaded);
		}

		return pageToBeLoaded;
	}

	/**
	 * Choose to append wildcard param to search query for a deeper search.
	 * @param {boolean} appendWildcardQuery
	 * @returns void
	 */
	public enableWildCardQuery(appendWildcardQuery: boolean): void {
		this.appendWildcardQuery = appendWildcardQuery;
	}

	/**
	 * Set user selected page
	 * @param {number} page
	 * @returns {void}
	 */
	public setUserSelectedPage(page: number): void {
		this.userSelectedPage = page;
	}

	/**
	 * Gets the user selected page
	 * @return {number} page
	 */
	public getUserSelectedPage(): number {
		return this.userSelectedPage;
	}

	/**
	 * Reset to first page
	 * @returns {void}
	 */
	public resetToFirstPage(): void {
		ViewportMgr.scrollTo(0);

		if (this.searchType === ESearchType.PAGE) {
			this.setPageNumber(FIRST_PAGE);
		}
	}

	/**
	 * Gets the user selected sorting
	 * @returns {String}
	 */
	public getUserSelectedSorting(): string {
		return this.userSelectedSorting;
	}

	/**
	 * Marks a flag that indicates that locale has been changed
	 * @param {boolean} changedLocale
	 */
	setChangedLocale(changedLocale: boolean) {
		this.changedLocale = changedLocale;
	}

	/**
	 * Returns changed locale flag
	 * @returns boolean
	 */
	getChangedLocale() {
		return this.changedLocale;
	}

	/**
	 * Marks a flag that indicates that
	 * a search redirect in another language has been done
	 * @param {boolean} isSearchRedirectInAnotherLang
	 */
	setIsSearchRedirectInAnotherLang(isSearchRedirectInAnotherLang: boolean) {
		this.isSearchRedirectInAnotherLang = isSearchRedirectInAnotherLang;
	}

	/**
	 * Returns if search redirect in another language
	 * @returns boolean
	 */
	getIsSearchRedirectInAnotherLang() {
		return this.isSearchRedirectInAnotherLang;
	}

	/**
	* Returns search params needed for refinements translation
	*
	* @param {string | undefined} cgid category id
	* @param {string | undefined} searchQuery query search
	* @returns ProductSearchParams
	*/
	protected getSearchParamTranslation(cgid: string | undefined, searchQuery: string): ProductSearchParams {
		const includeProductSets = getConfig()?.app?.preferences?.searchIncludeProductSets ?? false;
		const htype = includeProductSets ? ['variation_group', 'set'] : ['variation_group'];

		const tmpRefinements = {
			htype
		};

		if (cgid) {
			tmpRefinements['cgid'] = [cgid];
		}

		const refString = Object.keys(tmpRefinements || {})
			.map((key) => `${key}=${encodeURIComponent(tmpRefinements[key].join('|'))}`);
		const searchParams = {
			refine: refString,
			offset: 0,
			limit: 1,
			q: undefined
		};
		const storedSearchQuery = this.getStoredSearchModel().getSearchQuery();
		const query = storedSearchQuery ? storedSearchQuery : searchQuery;

		if (query) {
			searchParams.q = query;
		}

		return searchParams as ProductSearchParams;
	}

	/**
	 * Updates grid size
	 * @param {number} gridSize
	 * @returns {void}
	 */
	public updateGridSize(gridSize: number): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(storeGridSize({key: this.cacheKey, gridSize}));
	}

	/**
	 * Translate refinements and url when
	 * redirect in a different language triggers
	 *
	 * @param {string | undefined} cgid category id
	 * @param {object} refinements current search refinements
	 * @param {string | undefined} searchQuery search query
	 * @param {string} languageCodeFromUrl language code in the url
	 * @param {string} currentLanguageCode current language code
	 *
	 * @returns Promise<void>
	 */
	public abstract handleSearchRedirectInAnotherLang(
		cgid: string,
		refinements: object,
		searchQuery: string | undefined,
		languageCodeFromUrl: string,
		currentLanguageCode: string
	): Promise<void>;

	/**
	 * Returns the type of the search provider
	 * @returns {ESearchProvider}
	 */
	public abstract getProviderType(): ESearchProvider;
}

export default AbstractSearchProvider;
